import React from 'react';

export type ErrorBoundaryProps = {
  /**
   * What is being wrapped by ErrorBoundary
   */
  children: React.ReactNode;
  /**
   * This is the component that you would like to be rendered in the case that an error is triggered
   * (i.e. an `ErrorPage` component).
   */
  FallbackComponent?: React.ComponentType;
  /**
   * Actions you want to call when an error is triggered (i.e. `logger.error()`).
   */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
};

/**
 * # ErrorBoundary
 *
 * A CanvasApp-specific error boundary component that catches JavaScript errors anywhere in the child
 * component tree and can display a fallback UI. This is a standalone implementation for Canvas app
 * to avoid Spring dependencies.
 *
 * ## Usage
 * ```jsx
 * const Example = () => {
 *   return (
 *     <ErrorBoundary
 *       onError={(error, errorInfo) => {
 *         console.error(error);
 *       }}
 *       FallbackComponent={ErrorPage}
 *     >
 *       <CanvasComponent />
 *     </ErrorBoundary>
 *   );
 * };
 * ```
 *
 * @component
 */
export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  {
    hasError: boolean;
  }
> {
  static getDerivedStateFromError: () => {hasError: boolean};

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {hasError: false};
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const {onError} = this.props;
    onError && onError(error, errorInfo);
  }

  render() {
    const {hasError} = this.state;
    const {FallbackComponent = React.Fragment, children} = this.props;

    if (hasError) {
      return <FallbackComponent />;
    }

    return children;
  }
}

ErrorBoundary.getDerivedStateFromError = () => ({hasError: true});

export default ErrorBoundary;
